import React from 'react'
import {
  AriaDialogProps,
  mergeProps,
  useDialog,
  useModalOverlay,
  useOverlayTrigger,
} from 'react-aria'
import { OverlayTriggerState, useOverlayTriggerState } from 'react-stately'
import { AnimatePresence, motion } from 'framer-motion'
import styled, { useTheme } from 'styled-components'
import { ThemeProviderClean } from '../../../ThemeProviderClean'
import { AriaProps, PositioningProps } from '../../components/types'
import { Box, Flex } from '../box'
import { SafeFocusScope } from '../../internal-helpers/SafeFocusScope'
import { RootPortal } from '../portal/RootPortal'
import {
  cssToFramerMotionCubicBezier,
  cssToFramerMotionDuration,
} from '../../internal-helpers/cssToFramerMotion'
import { validatePositioningProps } from '../../helpers/validatePositioningProps'

type LabelProps =
  | {
      'aria-label': string
      'aria-labelledby'?: never
    }
  | {
      'aria-label'?: never
      'aria-labelledby': string
    }

type SizeProps = {
  h?: number | string
  w?: number | string
}

type Props = PositioningProps &
  SizeProps &
  LabelProps & {
    animation?: 'fadeInFromBottom' | 'slideFromRight'
    children: React.ReactNode
    isClickOutsideDismissable?: boolean
    isKeyboardDismissable?: boolean
    isOpen?: boolean
    onOpenChange?: (isOpen: boolean) => void
    trigger: ((triggerProps: AriaProps) => React.ReactNode) | undefined
  }

export const Modal = ({
  children,
  trigger,
  isOpen,
  onOpenChange,
  isKeyboardDismissable = true,
  isClickOutsideDismissable = true,
  animation,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  h,
  w,
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps)
  const state = useOverlayTriggerState({ isOpen, onOpenChange })

  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, state)

  return (
    <>
      {trigger?.({ ...triggerProps })}
      <RootPortal>
        <AnimatePresence>
          {state.isOpen && (
            <ThemeProviderClean>
              <ModalOverlay
                animation={animation}
                state={state}
                dialogProps={mergeProps(overlayProps, {
                  'aria-label': ariaLabel,
                  'aria-labelledby': ariaLabelledBy,
                })}
                isKeyboardDismissable={isKeyboardDismissable}
                isClickOutsideDismissable={isClickOutsideDismissable}
                w={w}
                h={h}
                {...positioningProps}
              >
                {children}
              </ModalOverlay>
            </ThemeProviderClean>
          )}
        </AnimatePresence>
      </RootPortal>
    </>
  )
}

interface ModalOverlayProps extends PositioningProps, SizeProps {
  animation: Props['animation']
  children: React.ReactNode
  dialogProps: AriaDialogProps
  isClickOutsideDismissable: boolean
  isKeyboardDismissable: boolean
  state: OverlayTriggerState
}

const ModalOverlay = ({
  children,
  state,
  dialogProps: dialogInputProps,
  isKeyboardDismissable,
  isClickOutsideDismissable,
  animation,
  w,
  h,
  ...positioningProps
}: ModalOverlayProps) => {
  validatePositioningProps(positioningProps)
  const ref = React.useRef(null)

  const { dialogProps } = useDialog(dialogInputProps, ref)
  const { modalProps, underlayProps } = useModalOverlay(
    { isDismissable: isClickOutsideDismissable, isKeyboardDismissDisabled: !isKeyboardDismissable },
    state,
    ref
  ) // ariaHideOutside is implicitly part of useModalOverlay

  const { easings, durations } = useTheme()
  const hasAnimation = animation !== undefined
  const transition = {
    ease: cssToFramerMotionCubicBezier(easings['standard']),
    duration: cssToFramerMotionDuration(
      durations[animation === 'slideFromRight' ? 'slow-2' : 'moderate-2']
    ),
  }

  const exitTransition = {
    ease: cssToFramerMotionCubicBezier(easings['standard']),
    duration: cssToFramerMotionDuration(
      durations[animation === 'slideFromRight' ? 'slow-1' : 'moderate-1']
    ),
  }

  return (
    <SafeFocusScope contain restoreFocus>
      <Flex
        position='fixed'
        zIndex={100000 /* match the zIndex of Popover */}
        top={0}
        left={0}
        bottom={0}
        right={0}
        flexDirection='column'
        alignItems='center'
      >
        <MotionFlex
          position='absolute'
          zIndex={-1}
          bg='bg/dim-overlay'
          top={0}
          left={0}
          bottom={0}
          right={0}
          initial={hasAnimation ? { opacity: 0 } : undefined}
          animate={hasAnimation ? { opacity: 1, transition } : undefined}
          exit={hasAnimation ? { opacity: 0, transition: exitTransition } : undefined}
          {...underlayProps}
        />
        <Box flexGrow={4} />
        <StyledWrapper
          initial={
            hasAnimation
              ? {
                  y: animation === 'slideFromRight' ? 0 : 200,
                  x: animation === 'slideFromRight' ? '100%' : 0,
                  opacity: animation === 'slideFromRight' ? 1 : 0,
                }
              : animation
          }
          animate={hasAnimation ? { y: 0, x: 0, opacity: 1, transition } : undefined}
          exit={
            hasAnimation
              ? {
                  y: 0,
                  x: animation === 'slideFromRight' ? '100%' : 0,
                  opacity: animation === 'slideFromRight' ? 1 : 0,
                  transition: exitTransition,
                }
              : undefined
          }
          h={h}
          w={w}
          maxWidth='100%'
          {...positioningProps}
          overflow='hidden'
          {...mergeProps(dialogProps, modalProps)}
          ref={ref}
        >
          {children}
        </StyledWrapper>
        <Box flexGrow={6} />
      </Flex>
    </SafeFocusScope>
  )
}

const MotionFlex = motion(Flex)

const StyledWrapper = styled(motion(Box))`
  :focus {
    outline: none;
  }
`
